import * as prettier from 'prettier/standalone';
import * as parserBabel from 'prettier/plugins/babel';
import * as parserHtml from 'prettier/plugins/html';
import * as parserMarkdown from 'prettier/plugins/markdown';
// @ts-ignore - estree is required by babel parser but has type issues
import * as parserEstree from 'prettier/plugins/estree';
import type { FileType } from '../components/Editor';

export interface FormatResult {
  success: boolean;
  formatted?: string;
  error?: string;
}

// Format code using Prettier
export async function formatCode(code: string, language: string): Promise<FormatResult> {
  try {
    const parser = getParserForLanguage(language);
    if (!parser) {
      return { success: false, error: `No formatter available for ${language}` };
    }

    const formatted = await prettier.format(code, {
      parser,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      plugins: [parserBabel, parserEstree, parserHtml, parserMarkdown] as any[],
      tabWidth: 4,
      useTabs: false,
      semi: true,
      singleQuote: true,
      trailingComma: 'es5',
      printWidth: 100,
    });

    return { success: true, formatted };
  } catch (e) {
    const error = e as Error;
    return { success: false, error: error.message };
  }
}

// Get the appropriate parser based on language
function getParserForLanguage(language: string): string | null {
  const parserMap: Record<string, string> = {
    'javascript': 'babel',
    'js': 'babel',
    'jsx': 'babel',
    'typescript': 'babel-ts',
    'ts': 'babel-ts',
    'tsx': 'babel-ts',
    'json': 'json',
    'html': 'html',
    'markdown': 'markdown',
    'md': 'markdown',
  };

  return parserMap[language.toLowerCase()] || null;
}

// Format a code block within markdown content
export async function formatCodeBlock(
  content: string,
  cursorPos: number
): Promise<{ success: boolean; formatted?: string; error?: string; from?: number; to?: number }> {
  // Find the code block containing the cursor
  const codeBlockRegex = /```(\w*)\n([\s\S]*?)```/g;
  let match;

  while ((match = codeBlockRegex.exec(content)) !== null) {
    const blockStart = match.index;
    const blockEnd = match.index + match[0].length;

    // Check if cursor is within this code block
    if (cursorPos >= blockStart && cursorPos <= blockEnd) {
      const language = match[1].toLowerCase() || 'text';
      const codeContent = match[2];

      // Calculate positions for the code content (excluding ``` markers)
      const codeStart = blockStart + 3 + match[1].length + 1; // After ```lang\n
      const codeEnd = blockEnd - 3; // Before closing ```

      // Try to format
      const result = await formatCode(codeContent, language);

      if (result.success && result.formatted) {
        // Trim trailing newline that prettier adds
        const trimmedFormatted = result.formatted.replace(/\n$/, '');
        return {
          success: true,
          formatted: trimmedFormatted,
          from: codeStart,
          to: codeEnd
        };
      }

      return { success: false, error: result.error || `Cannot format ${language}` };
    }
  }

  return { success: false, error: 'Cursor is not inside a code block' };
}

// Check if a language is supported for formatting
export function isFormatSupported(fileType: FileType): boolean {
  const supported: FileType[] = ['javascript', 'typescript', 'json', 'markdown'];
  return supported.includes(fileType);
}

// Format entire file based on file type
export async function formatFile(content: string, fileType: FileType): Promise<FormatResult> {
  if (!isFormatSupported(fileType)) {
    return { success: false, error: `Formatting not supported for ${fileType}` };
  }

  return formatCode(content, fileType);
}
